import { GoX, GoCheck, GoChevronDown } from "react-icons/go";
import { useState, useEffect } from 'react';
import { Combobox } from '@headlessui/react'

/**
 * Displays the Publisher filter section.
 * 
 * @param {*} props Contains 'filters' state variable & 'setFilters' state method.
 */
export default function PublisherFilter(props) {
  const [inputValue, setInputValue] = useState("");
  const [chips, setChips] = useState(null);
  const [selectedPublisher, setSelectedPublisher] = useState("");
  const [allowInputValueChange, setAllowInputValueChange] = useState(true);
  const [publisherChoices, setPublisherChoices] = useState([]);

  const publisherFilters = props.filters.publisher;
  const setFilters = props.setFilters;

  // Load publisher choices from localStorage and watch for updates
  useEffect(() => {
    const loadPublisherChoices = () => {
      try {
        const attributesStr = localStorage.getItem('attributes');
        if (attributesStr) {
          const attributesData = JSON.parse(attributesStr);
          if (attributesData && attributesData.filterOptions && Array.isArray(attributesData.filterOptions)) {
            let publishers = attributesData.filterOptions.filter(filterOption => filterOption.category === "publisher" && filterOption.name !== '');
            publishers = publishers.filter((value, index, self) => self.findIndex(value2 => (value2.name === value.name)) === index);
            setPublisherChoices(publishers);
          }
        }
      } catch (e) {
        console.error('Error parsing attributes from localStorage:', e);
      }
    };

    // Load immediately
    loadPublisherChoices();

    // Listen for custom event when attributes are updated
    const handleAttributesUpdate = () => {
      loadPublisherChoices();
    };
    window.addEventListener('localStorage:attributesUpdated', handleAttributesUpdate);

    // Listen for storage events (when localStorage is updated from another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === 'attributes') {
        loadPublisherChoices();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('localStorage:attributesUpdated', handleAttributesUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  //Takes publisherChoices and filters them based on what the user has entered into the publisher search input
  const filteredpublisherChoices =
    inputValue === ''
      ? publisherChoices
      : publisherChoices.filter((publisherChoice) => {
        return publisherChoice.name.toLowerCase().includes(inputValue.toLowerCase())
      })


  //Populates the combobox input with the user's publisher selection from the combobox dropdown
  useEffect(() => {
    if (typeof selectedPublisher !== 'undefined' && selectedPublisher !== '' && selectedPublisher !== null) {
      // Without this, when you select an item from the dropdown, it adds a chip for the item but then also populates the input (bad). 
      // This makes it so the chip is added and the input remains empty by checking if input value is allowed to be added.
      if (allowInputValueChange) {
        setInputValue(selectedPublisher.name);
      }
      setAllowInputValueChange(true);
    }
  }, [selectedPublisher]);


  // Everytime the publisher in the 'filters' state is updated it re-renders the chips
  useEffect(() => {
    displayChips();
  }, [publisherFilters]);


  /**
   * Iterates through the publisher property of the 'filters' state object to create chips and stores them in 'chips' state variable.
   */
  function displayChips() {
    if (publisherFilters !== '' && publisherFilters.length > 0) {
      setChips(
        publisherFilters.map((publisherFilter, index) => {
          return (
            <span
              key={publisherFilter || `publisher-${index}`}
              className="break-all px-3 py-2 rounded-lg text-white bg-blue-700 font-semibold text-sm flex items-center w-max cursor-pointer active:bg-gray-300 transition duration-300 ease hover:bg-blue-600"
            >
              {publisherFilter}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  deletePublisher(publisherFilter);
                }}
                className="bg-transparent hover:bg-blue-600 focus:outline-none ml-2 p-0.5 rounded"
                aria-label="Remove publisher"
              >
                <GoX className="h-4 w-4 text-white" aria-hidden="true" />
              </button>
            </span>
          )
        })
      );
    } else {
      setChips(null);
    }
  }


  /**
   * Adds a publisher to the 'filters.publisher' state variable
   */
  function addPublisher(publisher) {
    setSelectedPublisher('');
    let trimmedInputValue;

    if (publisher !== undefined) {
      trimmedInputValue = publisher.trim();
    } else {
      trimmedInputValue = JSON.parse(JSON.stringify(inputValue)).trim()
    }
    if (trimmedInputValue !== '') {
      setFilters(prevState => {
        const filters = {
          ...prevState,
          publisher: [...prevState.publisher, trimmedInputValue]
        }

        props.setParam("publisher", filters.publisher);
        return filters;
      });
      setInputValue("");
    }
  }


  /**
   * Removes the specified publisher from the 'filters' state variable.
   * 
   * @param {*} publisher Publisher to remove from state.
   */
  function deletePublisher(publisher) {
    setInputValue("");
    let updatedPublisherFilter = [...publisherFilters];
    updatedPublisherFilter.splice(updatedPublisherFilter.indexOf(publisher), 1);

    setFilters(prevState => {
      const filters = {
        ...prevState,
        publisher: updatedPublisherFilter
      }

      if(!filters.publisher.length){
        props.removeParam("publisher");
      }
      else{
        props.setParam("publisher", filters.publisher);
      }
      return filters;
    });
  }


  //Joins strings. Used for combobox option hover styles.
  function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
  }

  return (
    <div> 
      {/* User can select only 1 publisher. When a publisher is added to the filters state variable, combobox is removed to prevent the user from adding more publishers. */}
      {publisherFilters.length === 0 &&
      <div className="sm:flex sm:items-center">
        <Combobox as="div" className="w-full sm:max-w-xs" value={selectedPublisher} onChange={setSelectedPublisher} nullable>
          <div className="relative">
            <Combobox.Input
              value={inputValue}
              data-cy="publisher"
              className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              onChange={(event) => { setInputValue(event.target.value) }}
              maxLength={250}
              placeholder="Search Publishers"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  addPublisher();
                }
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  setInputValue("");
                }
              }}
            />
            
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
              <GoChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>

            {filteredpublisherChoices.length > 0 && (
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-gray-800 dark:border-gray-700">
                {filteredpublisherChoices.map((filteredpublisherChoice) => (
                  <Combobox.Option
                    onClick={(e) => {
                      addPublisher(filteredpublisherChoice.name)
                      setAllowInputValueChange(false);
                    }}
                    key={filteredpublisherChoice.PK}
                    value={filteredpublisherChoice}
                    className={({ active }) =>
                      classNames(
                        'relative cursor-default select-none py-2 pl-3 pr-9',
                        active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-gray-200'
                      )
                    }
                  >
                  <span>{filteredpublisherChoice.name}</span>
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
          </div>
        </Combobox>
        <button
          disabled={inputValue.length === 0 ? true : false}
          data-cy="publisher-add"
          type="button"
          className={
            classNames(
              "w-full mt-2 inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm",
              inputValue.length === 0 ? "bg-gray-300 cursor-default dark:bg-gray-800" : "bg-blue-600 hover:bg-blue-700"
            )
          }
          onClick={() => addPublisher()}
        >
          <span className="sr-only">Add publisher</span>
          <GoCheck className={classNames("h-4 w-4 text-white", inputValue.length === 0 ? "dark:text-gray-400" : "dark:text-white")} aria-hidden="true" />
        </button>
      </div>
      }

      {/* Displays publisher chip */}
      <div className="mt-0 flex flex-wrap">
        {chips}
      </div>

    </div>
  )
}